import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { mockAPI } from '@/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Clock, MapPin, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateWithDay, formatTime12Hour, formatTimeRange } from '@/utils/formatters';

const MockExamTab = () => {
  const { refreshCredits, credits, moduleCredits } = useAuth();
  const [mocks, setMocks] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSpeakingModal, setShowSpeakingModal] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedMock, setSelectedMock] = useState(null);
  const [speakingSlots, setSpeakingSlots] = useState([]);
  const [selectedSpeakingSlot, setSelectedSpeakingSlot] = useState(null);
  const [speakingMode, setSpeakingMode] = useState('OFFLINE');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [selectedMockBooking, setSelectedMockBooking] = useState(null);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchMocks();
    fetchMyBookings();
  }, [refreshKey]);
  
  useEffect(() => {
    // Listen for cancellation events from My Bookings tab
    const handleBookingCancelled = () => {
      console.log('Booking cancelled event received - refreshing mock list');
      setRefreshKey(prev => prev + 1); // Force re-fetch
    };
    
    window.addEventListener('bookingCancelled', handleBookingCancelled);
    
    return () => {
      window.removeEventListener('bookingCancelled', handleBookingCancelled);
    };
  }, []);

  const fetchMocks = async () => {
    setLoading(true);
    try {
      const response = await mockAPI.getList();
      setMocks(response.data);
    } catch (error) {
      console.error('Failed to fetch mocks:', error);
      toast.error('Failed to load mock tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBookings = async () => {
    try {
      const response = await mockAPI.getMyBookings();
      setMyBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const handleBookMock = async (mock) => {
    // Check if user has sufficient MOCK credits before showing speaking slots
    if (moduleCredits.mock <= 0) {
      toast.error(`Insufficient mock credits. You have ${moduleCredits.mock} credits remaining.`);
      return;
    }
    
    try {
      // First, fetch speaking slots for this mock
      const slotsResponse = await mockAPI.getSpeakingSlots(mock.id);
      setSpeakingSlots(slotsResponse.data);
      setSelectedMock(mock);
      setShowSpeakingModal(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to load speaking slots';
      toast.error(errorMsg);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSpeakingSlot(slot);
    
    // Show mode selection modal for all speaking slots
    setShowSpeakingModal(false);
    setShowModeModal(true);
  };

  const handleModeConfirm = () => {
    // Validate WhatsApp number if online mode selected
    if (speakingMode === 'ONLINE') {
      // Remove any non-digit characters for validation
      const digitsOnly = whatsappNumber.replace(/\D/g, '');
      
      if (!digitsOnly || digitsOnly.length === 0) {
        toast.error('WhatsApp number অবশ্যই প্রয়োজন (Required)');
        return;
      }
      
      if (digitsOnly.length !== 11) {
        toast.error('WhatsApp number অবশ্যই 11 digits হতে হবে (যেমন: 01712345678)');
        return;
      }
    }
    
    // Show confirmation modal with all details
    setShowModeModal(false);
    setShowConfirmModal(true);
  };

  const handleFinalConfirm = async () => {
    setBookingInProgress(true);
    
    try {
      // Step 1: Book LRW portion
      const lrwResponse = await mockAPI.bookLRW(selectedMock.id);
      const mockBookingId = lrwResponse.data.id;
      
      // Step 2: Book Speaking slot
      await mockAPI.bookSpeaking(
        mockBookingId,
        selectedSpeakingSlot.id,
        speakingMode,
        speakingMode === 'ONLINE' ? whatsappNumber : null
      );
      
      toast.success('Mock test booked successfully!');
      
      // Reset states
      setShowConfirmModal(false);
      setSelectedMock(null);
      setSelectedSpeakingSlot(null);
      setSpeakingMode('OFFLINE');
      setWhatsappNumber('');
      
      // Refresh data
      await fetchMocks();
      await fetchMyBookings();
      await refreshCredits();
    } catch (error) {
      console.error('Mock booking error:', error);
      let errorMsg = 'Failed to book mock test';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg = detail[0]?.msg || errorMsg;
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setBookingInProgress(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Credits Display */}
      <Card className="backdrop-blur-lg bg-gradient-to-br from-blue-50 to-purple-50 shadow-lg border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <Coins className="h-7 w-7 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Mock Test Credits</CardTitle>
                <CardDescription className="text-sm text-gray-600 mt-0.5">
                  Available credits for booking
                </CardDescription>
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-blue-600">{String(moduleCredits?.mock || 0)}</div>
              <p className="text-sm text-gray-600 font-medium mt-1">Remaining</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Mock Tests List */}
      <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0">
        <CardHeader>
          <CardTitle>Available Mock Tests</CardTitle>
          <CardDescription>Book your complete IELTS mock test (L/R/W + Speaking)</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : mocks.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg font-semibold">No mock tests available</p>
              <p className="text-gray-500 mt-2">Please check back later or contact admin</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mocks.map((mock) => {
                const isBooked = myBookings.some(b => b.mock_id === mock.id && b.lrw_status === 'BOOKED');
                const isLRWFull = mock.lrw_remaining_seats === 0;
                const isSpeakingFull = mock.speaking_remaining_slots === 0;
                const isFull = isLRWFull || isSpeakingFull;
                
                return (
                  <div
                    key={mock.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isBooked
                        ? 'bg-green-50 border-green-300'
                        : isFull
                        ? 'bg-red-50 border-red-300'
                        : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-300 hover:shadow-lg'
                    }`}
                    data-testid={`mock-${mock.id}`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge className={isBooked ? 'bg-green-600' : isFull ? 'bg-red-600' : 'bg-blue-600'}>
                          {isBooked ? 'Your Booking' : isFull ? 'Full' : 'Available'}
                        </Badge>
                        {!isBooked && (
                          <div className={`text-sm font-semibold ${mock.lrw_remaining_seats > 5 ? 'text-green-600' : 'text-red-600'}`}>
                            {mock.lrw_remaining_seats} Seats Left
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-1">
                        <div className="text-lg font-bold text-blue-600">{mock.mock_uid}</div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="font-semibold">{formatDateWithDay(mock.lrw_date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span>{formatTimeRange(mock.lrw_start, mock.lrw_end)}</span>
                        </div>
                        {mock.campus && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span>{mock.campus}</span>
                          </div>
                        )}
                      </div>

                      {!isBooked && !isFull && (
                        <Button
                          onClick={() => handleBookMock(mock)}
                          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 mt-3"
                          size="sm"
                        >
                          Book Mock Test
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Speaking Slots Modal */}
      <Dialog open={showSpeakingModal} onOpenChange={setShowSpeakingModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Speaking Slot</DialogTitle>
            <DialogDescription>
              Choose a speaking slot for mock test: {selectedMock?.mock_uid}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {speakingSlots.map((slot) => {
              const isFull = slot.is_booked;
              
              return (
                <div
                  key={slot.id}
                  onClick={() => !isFull && handleSlotSelect(slot)}
                  className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    isFull
                      ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-300 opacity-80 cursor-not-allowed'
                      : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-300 hover:shadow-lg hover:scale-105'
                  }`}
                >
                  <div className="space-y-2">
                    <Badge className={isFull ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white' : 'bg-blue-600'}>
                      {isFull ? 'This Slot is Taken' : 'Available'}
                    </Badge>
                    
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold">
                          {formatDateWithDay(slot.speak_date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>{formatTimeRange(slot.start_time, slot.end_time)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Speaking Mode Selection Modal */}
      <Dialog open={showModeModal} onOpenChange={setShowModeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Speaking Mode</DialogTitle>
            <DialogDescription>
              Choose how you want to take the speaking test
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Mode Selection */}
            <div className="space-y-3">
              <button
                onClick={() => setSpeakingMode('OFFLINE')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  speakingMode === 'OFFLINE'
                    ? 'bg-blue-50 border-blue-500'
                    : 'bg-gray-50 border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">🏢</div>
                  <div>
                    <div className="font-semibold">Offline Mode</div>
                    <div className="text-sm text-gray-600">Face-to-face at test center</div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setSpeakingMode('ONLINE')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  speakingMode === 'ONLINE'
                    ? 'bg-blue-50 border-blue-500'
                    : 'bg-gray-50 border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">📱</div>
                  <div>
                    <div className="font-semibold">Online Mode</div>
                    <div className="text-sm text-gray-600">Via WhatsApp call</div>
                  </div>
                </div>
              </button>
            </div>

            {/* WhatsApp Number Input (only for Online mode) */}
            {speakingMode === 'ONLINE' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  WhatsApp Number <span className="text-red-600">*</span>
                </label>
                <input
                  type="tel"
                  placeholder="01712345678 (11 digits)"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  maxLength={11}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500">
                  <span className="text-red-600 font-medium">Required:</span> আপনার WhatsApp number (অবশ্যই 11 digits)
                </p>
                
                {/* Important Notice for Online Speaking */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-sm text-blue-800 font-medium mb-1">📱 Important Notice:</p>
                  <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                    <li>আপনার নেটওয়ার্ক যেন স্থিতিশীল থাকে তা নিশ্চিত করুন</li>
                    <li>আমরা সময়মতো WhatsApp এ কল দিব, ইনশাআল্লাহ</li>
                    <li>সময়মতো কল পিক আপ না করলে অনুপস্থিত ধরে নেয়া হবে</li>
                  </ul>
                </div>
              </div>
            )}

            <Button
              onClick={handleModeConfirm}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Booking</DialogTitle>
            <DialogDescription>
              Please review your mock test booking details
            </DialogDescription>
          </DialogHeader>
          
          {selectedMock && selectedSpeakingSlot && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Mock Test Details</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Test:</strong> {selectedMock.mock_uid}</p>
                  <p><strong>L/R/W Date:</strong> {formatDateWithDay(selectedMock.lrw_date)}</p>
                  <p><strong>L/R/W Time:</strong> {formatTimeRange(selectedMock.lrw_start, selectedMock.lrw_end)}</p>
                  <p><strong>Campus:</strong> {selectedMock.campus}</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Speaking Details</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Date:</strong> {formatDateWithDay(selectedSpeakingSlot.speak_date)}</p>
                  <p><strong>Time:</strong> {formatTimeRange(selectedSpeakingSlot.start_time, selectedSpeakingSlot.end_time)}</p>
                  <p className="flex items-center gap-2">
                    <strong>Mode:</strong> 
                    <Badge className={speakingMode === 'ONLINE' ? 'bg-green-600' : 'bg-blue-600'}>
                      {speakingMode === 'ONLINE' ? '📱 Online' : '🏢 Offline'}
                    </Badge>
                  </p>
                  {speakingMode === 'ONLINE' && (
                    <p><strong>WhatsApp:</strong> {whatsappNumber}</p>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm text-yellow-800">
                ⚠️ This will deduct 1 mock test credit from your account
              </div>

              <Button
                onClick={handleFinalConfirm}
                disabled={bookingInProgress}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600"
              >
                {bookingInProgress ? 'Booking...' : 'Confirm Booking'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MockExamTab;